import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, AlertOctagon, Trash2, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const CATEGORIES = [
  { value: "breakdown",   label: "Breakdown",           color: "bg-red-100 text-red-800" },
  { value: "changeover",  label: "Changeover",          color: "bg-amber-100 text-amber-800" },
  { value: "material",    label: "Material starvation", color: "bg-purple-100 text-purple-800" },
  { value: "quality",     label: "Quality hold",        color: "bg-sky-100 text-sky-800" },
  { value: "planned",     label: "Planned",             color: "bg-slate-200 text-slate-800" },
  { value: "other",       label: "Other",               color: "bg-secondary text-foreground" },
];
const EMPTY = { machine_id: "", category: "breakdown", reason: "", started_at: "", ended_at: "", notes: "" };

export default function GmesDowntime() {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/gmes/downtime").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/gmes/machines").then(({ data }) => setMachines(data)).catch(() => {});
  }, []);

  const machineMap = useMemo(() => Object.fromEntries(machines.map((m) => [m.id, m.name])), [machines]);
  const open_events = items.filter((d) => !d.ended_at);
  const closed = items.filter((d) => d.ended_at);
  const totalOpenMinutes = open_events.reduce((a, d) => {
    if (!d.started_at) return a;
    const diff = (Date.now() - new Date(d.started_at).getTime()) / 60000;
    return a + Math.max(0, diff);
  }, 0);

  const save = async (e) => {
    e.preventDefault();
    if (!form.machine_id || !form.reason.trim()) { toast.error("Machine and reason required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.started_at) delete payload.started_at;
      if (!payload.ended_at) delete payload.ended_at;
      await api.post("/gmes/downtime", payload);
      toast.success("Downtime logged");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const closeEvent = async (id) => {
    try {
      await api.patch(`/gmes/downtime/${id}/close`, {});
      toast.success("Event closed");
      load();
    } catch { toast.error("Close failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/gmes/downtime/${id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/shopfloor" className="hover:text-primary">Shop Floor</Link> / Downtime
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="gmes-downtime-title">
            Downtime Tracker
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Log breakdowns, changeovers, material starvation and quality holds. Track Pareto by cause.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-downtime-btn">
          <Plus className="w-4 h-4 mr-2" /> Log downtime
        </Button>
      </div>

      {/* summary */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border bg-card rounded-sm p-4"><div className="overline text-muted-foreground">Open events</div><div className="font-display font-black text-3xl mt-1 tabular-nums">{open_events.length}</div></div>
        <div className="border border-border bg-card rounded-sm p-4"><div className="overline text-muted-foreground">Open minutes</div><div className="font-display font-black text-3xl mt-1 tabular-nums">{Math.round(totalOpenMinutes)}</div></div>
        <div className="border border-border bg-card rounded-sm p-4"><div className="overline text-muted-foreground">Closed events (all)</div><div className="font-display font-black text-3xl mt-1 tabular-nums">{closed.length}</div></div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <AlertOctagon className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No downtime events</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Downtime opens automatically when a machine is set to <em>Down</em>. You can also log it manually here.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                {["Machine", "Category", "Reason", "Started", "Ended", "Minutes", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const cat = CATEGORIES.find((c) => c.value === d.category) || CATEGORIES[5];
                return (
                  <tr key={d.id} className="border-t border-border" data-testid={`dt-row-${d.id}`}>
                    <td className="px-3 py-2 font-medium">{machineMap[d.machine_id] || "—"}</td>
                    <td className="px-3 py-2"><Badge className={`rounded-sm ${cat.color} hover:${cat.color} font-mono-plex text-[10px] uppercase tracking-widest`}>{cat.label}</Badge></td>
                    <td className="px-3 py-2 text-sm">{d.reason}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {d.started_at ? formatDistanceToNow(new Date(d.started_at), { addSuffix: true }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {d.ended_at ? formatDistanceToNow(new Date(d.ended_at), { addSuffix: true }) : <span className="text-red-600 font-mono-plex uppercase tracking-widest">Open</span>}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-xs">{d.duration_minutes != null ? `${d.duration_minutes}m` : "—"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!d.ended_at && (
                        <button title="Close event" onClick={() => closeEvent(d.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-sm" data-testid={`dt-close-${d.id}`}>
                          <CheckSquare className="w-4 h-4" />
                        </button>
                      )}
                      <button title="Delete" onClick={() => remove(d.id)} className="p-1 text-muted-foreground hover:text-destructive hover:bg-red-50 rounded-sm ml-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-sm">
          <DialogHeader>
            <div className="overline text-accent">◉ LOG DOWNTIME</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">New event</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Machine *</label>
              <Select value={form.machine_id} onValueChange={(v) => setForm({ ...form, machine_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="dt-machine-select"><SelectValue placeholder="Choose machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Reason *</label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-sm" data-testid="dt-reason-input" required placeholder="Motor failure, waiting on ink, changeover to job 1234…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Started (blank = now)</label>
                <Input type="datetime-local" value={form.started_at} onChange={(e) => setForm({ ...form, started_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Ended (optional)</label>
                <Input type="datetime-local" value={form.ended_at} onChange={(e) => setForm({ ...form, ended_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-sm" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-downtime-btn">
              {saving ? "Saving…" : "Log event"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
